import { forwardRef, useEffect, useImperativeHandle, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import {
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material'
import { makeStyles } from '@mui/styles'
import { Text } from '@pbt/pbt-ui-components'
import { Delete as DeleteIcon } from '@pbt/pbt-ui-components/src/icons'

import { NumberStepper } from '~/components/elements/NumberStepper/NumberStepper'
import DialogNames from '~/constants/DialogNames'
import FeatureToggle from '~/constants/featureToggle'
import { ShipmentItemSearchCriteria } from '~/constants/shipments'
import { useIsDrug } from '~/store/hooks/orders'
import { getFeatureToggle } from '~/store/reducers/constants'
import type { InventoryLineItem, Prescription, Variation } from '~/types'
import {
  getItemId,
  getItemKey,
  isInventoryItemTracking,
  type ShipmentItemUnion,
} from '~/utils/inventoryItemTrackingHelpers'
import { getPrescriptionName } from '~/utils/prescription'
import useDialog from '~/utils/useDialog'

const DEFAULT_LINE_ITEM_QUANTITY = 1

// We need these fields to maintain backward compatibility with the old data structure
const getDispensedFields = (item: Prescription) => ({
  lotNumber: item?.lotNumber,
  serialNumber: item?.serialNumber,
  businessReferenceNumber: item?.businessReferenceNumber,
  manufacturerId: item?.manufacturerId,
  manufacturerName: item?.manufacturerName,
  expirationDate: item?.expirationDate,
})

const mapPrescriptionToDispensed = (item: Prescription): DispensedItem[] => [
  {
    lotNumber: item.lotNumber,
    serialNumber: item.serialNumber,
    lineItemQuantity: item.totalQuantity,
    inventoryShipmentItemId: '',
    id: '',
    expirationDate: item.expirationDate,
  },
]

/**
 * Maps an InventoryLineItem to a DispensedItem, preserving the receipt vs shipment distinction.
 */
const mapInventoryLineItemToDispensed = (
  log: InventoryLineItem,
): DispensedItem => ({
  lotNumber: log.lotNumber,
  serialNumber: log.serialNumber,
  lineItemQuantity: log.lineItemQuantity,
  ...(log.inventoryOrderItemReceiptId
    ? { inventoryOrderItemReceiptId: log.inventoryOrderItemReceiptId }
    : { inventoryShipmentItemId: log.inventoryShipmentItemId! }),
})

/** Returns the effective ID for a DispensedItem (receipt or shipment). */
const getDispensedItemId = (item: DispensedItem): string =>
  item.inventoryOrderItemReceiptId ?? item.inventoryShipmentItemId

/** Returns a unique prefixed key to avoid collisions between receipt and shipment IDs. */
const getDispensedItemKey = (item: DispensedItem): string => {
  if (
    'inventoryOrderItemReceiptId' in item &&
    item.inventoryOrderItemReceiptId
  ) {
    return `ORDER_ITEM_RECEIPT:${item.inventoryOrderItemReceiptId}`
  }
  if ('inventoryShipmentItemId' in item && item.inventoryShipmentItemId) {
    return `SHIPMENT_ITEM:${item.inventoryShipmentItemId}`
  }
  return ''
}

/** Maps a DispensedItem to a Partial<ShipmentItemUnion> for dialog pre-selection. */
const mapDispensedItemToSelectedItem = (
  item: DispensedItem,
): Partial<ShipmentItemUnion> => ({
  amount: Number(item?.lineItemQuantity) || 0,
  lotNumber: item?.lotNumber,
  serialNumber: item?.serialNumber,
  expirationDate: item?.expirationDate,
  ...(item.inventoryOrderItemReceiptId
    ? {
        itemType: 'ORDER_ITEM_RECEIPT' as const,
        receiptId: item.inventoryOrderItemReceiptId,
      }
    : {
        itemType: 'SHIPMENT_ITEM' as const,
        shipmentItemId: item.inventoryShipmentItemId!,
      }),
})

const getIsPrescriptionHasFields = (fields: any) =>
  Object.values(fields).some(Boolean)

const useStyles = makeStyles(
  (theme) => ({
    tableHeading: {
      verticalAlign: 'top',
      padding: theme.spacing(1, 2),
    },
    tableViewport: {
      border: theme.constants.tableBorder,
    },
    tableCell: {
      padding: theme.spacing(0.5, 2),
      border: 'none',
    },
    tableRow: {
      '&:nth-of-type(even)': {
        backgroundColor: theme.colors.tableEvenItem,
      },
      '&:last-of-type': {
        borderTop: theme.constants.tableBorder,
        backgroundColor: theme.colors.tableBackground,
      },
    },
  }),
  { name: 'DispensedFromSection' },
)

export interface DispensedFromSectionProps {
  onFieldsChange: () => void
  prescribed?: number
  prescription: Prescription
}
type DispensedFields = ReturnType<typeof getDispensedFields>

type GetDispensedFromSection = DispensedFields & {
  lineItemQuantity?: number | string
}

export interface DispensedFromSectionHandle {
  chooseFromInventory: () => void
  get: () => DispensedItem[] | GetDispensedFromSection
}

export type DispensedItem = {
  expirationDate?: string
  id?: string
  lineItemQuantity?: number | string
  lotNumber?: string
  serialNumber?: string
} & (
  | {
      inventoryOrderItemReceiptId: string
      inventoryShipmentItemId?: never
    }
  | {
      inventoryOrderItemReceiptId?: never
      inventoryShipmentItemId: string
    }
)

const DispensedFromSection = forwardRef<
  DispensedFromSectionHandle,
  DispensedFromSectionProps
>(function DispensedFromSection(
  { onFieldsChange, prescription, prescribed: prescribedProp },
  ref,
) {
  const classes = useStyles()
  const { t } = useTranslation('Common')

  const isFoodCatalogEnabled = useSelector(
    getFeatureToggle(FeatureToggle.FOOD_CATALOG),
  )

  const [openShipmentItemsDialog] = useDialog(
    DialogNames.CHOOSE_SHIPMENT_ITEM_DIALOG,
  )

  const initialItems = prescription?.inventoryShipmentItemLogs?.length
    ? prescription.inventoryShipmentItemLogs.map(
        mapInventoryLineItemToDispensed,
      )
    : getIsPrescriptionHasFields(getDispensedFields(prescription))
      ? mapPrescriptionToDispensed(prescription)
      : []

  const [items, setItems] = useState<DispensedItem[]>(initialItems)

  useEffect(() => {
    onFieldsChange()
  }, [items])

  const variationId = prescription?.variation?.id

  const onInventoryChange = (
    props: ShipmentItemUnion[] | ShipmentItemUnion,
  ) => {
    if (!Array.isArray(props)) return
    const lineItems = props
      .filter((lineItem) => getItemId(lineItem))
      .map((lineItem) => {
        const itemId = getItemId(lineItem)
        const itemKey = getItemKey(lineItem)
        const initialItem =
          items.find((item) => getDispensedItemKey(item) === itemKey) ||
          ({} as DispensedItem)
        const isReceipt =
          isInventoryItemTracking(lineItem) &&
          lineItem.itemType === 'ORDER_ITEM_RECEIPT'
        const defaultQty =
          items.length === 0
            ? (prescribedProp ?? DEFAULT_LINE_ITEM_QUANTITY)
            : DEFAULT_LINE_ITEM_QUANTITY
        return {
          id: initialItem.id,
          lineItemQuantity:
            initialItem.lineItemQuantity != null
              ? initialItem.lineItemQuantity
              : defaultQty,
          lotNumber: lineItem?.lotNumber,
          serialNumber: lineItem?.serialNumber,
          expirationDate: lineItem?.expirationDate,
          ...(isReceipt
            ? { inventoryOrderItemReceiptId: itemId }
            : { inventoryShipmentItemId: itemId }),
        }
      })

    // When adding new items, keep total dispensed equal to prescribed by reducing from the first item(s)
    const total = lineItems.reduce(
      (acc, item) => acc + (Number(item.lineItemQuantity) || 0),
      0,
    )
    const excess = total - (prescribedProp ?? 0)
    const itemsToSet =
      prescribedProp != null && lineItems.length > 1 && excess > 0
        ? (() => {
            let remainingToRemove = excess
            return lineItems.map((item) => {
              const qty = Number(item.lineItemQuantity) || 0
              const take = Math.min(qty, remainingToRemove)
              remainingToRemove -= take
              return {
                ...item,
                lineItemQuantity: qty - take,
              }
            })
          })()
        : lineItems

    setItems(itemsToSet)
  }
  const isDrug = useIsDrug(prescription)

  const handleChooseFromInventory = () => {
    openShipmentItemsDialog({
      order: {
        name: getPrescriptionName(prescription, isFoodCatalogEnabled) || '',
        variation: { id: variationId } as Variation,
      },
      showToggle: !isDrug,
      isMultiSelect: true,
      selectedItems: (items || []).map(mapDispensedItemToSelectedItem),
      onSelect: onInventoryChange,
      searchCriteria: ShipmentItemSearchCriteria.INVENTORY,
    })
  }

  const handleAmountChange = (key: string, amount: number) => {
    // If key is empty, it means we're in legacy mode with a single item (no inventory tracking IDs).
    // In this case, we update the first (and only) item.
    if (!key) {
      setItems((prevItems) => {
        const updatedItems = [...prevItems]
        updatedItems[0].lineItemQuantity = amount
        return updatedItems
      })
    } else {
      setItems((prevItems) =>
        prevItems?.map((item) =>
          getDispensedItemKey(item) === key
            ? { ...item, lineItemQuantity: amount }
            : item,
        ),
      )
    }
  }

  const handleDelete = (key: string) => {
    setItems((prevItems) =>
      prevItems.filter((item) => getDispensedItemKey(item) !== key),
    )
  }

  useImperativeHandle(ref, () => ({
    get: () => {
      if (items.length === 1 && !getDispensedItemId(items[0])) {
        return {
          ...getDispensedFields(prescription),
          lineItemQuantity: items[0].lineItemQuantity,
        }
      }
      return items
    },
    chooseFromInventory: handleChooseFromInventory,
  }))

  if (!items?.length) {
    return null
  }

  const totalAmount = items?.reduce(
    (acc, item) => acc + (Number(item.lineItemQuantity) || 0),
    0,
  )

  return (
    <Table className={classes.tableViewport}>
      <TableHead>
        <TableRow>
          <TableCell className={classes.tableHeading}>
            <Text strong variant="lowAccent2">
              {t('Common:LOT_SHIPMENT_NUMBER')}
            </Text>
          </TableCell>
          <TableCell className={classes.tableHeading}>
            <Text strong variant="lowAccent2">
              {t('Common:SERIAL_NUMBER_SIGN')}
            </Text>
          </TableCell>
          <TableCell />
          <TableCell align="right" className={classes.tableHeading}>
            <Text strong variant="lowAccent2">
              {t('Common:QUANTITY_DISPENSED')}
            </Text>
          </TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {items.map((row) => (
          <TableRow
            className={classes.tableRow}
            key={getDispensedItemKey(row) || row.lotNumber}
          >
            <TableCell className={classes.tableCell}>{row.lotNumber}</TableCell>
            <TableCell className={classes.tableCell}>
              {row.serialNumber}
            </TableCell>
            <TableCell className={classes.tableCell}>
              <IconButton
                aria-label={t('Common:DELETE_ACTION')}
                onClick={() => handleDelete(getDispensedItemKey(row))}
              >
                <DeleteIcon />
              </IconButton>
            </TableCell>
            <TableCell
              className={classes.tableCell}
              sx={{ display: 'flex', justifyContent: 'flex-end' }}
            >
              <NumberStepper
                value={Number(row.lineItemQuantity) || 0}
                onChange={(newValue: number | string) =>
                  handleAmountChange(getDispensedItemKey(row), Number(newValue))
                }
              />
            </TableCell>
          </TableRow>
        ))}
        <TableRow className={classes.tableRow}>
          <TableCell align="right" className={classes.tableCell} colSpan={4}>
            <Text strong m={0.5} variant="subheading3">
              {t('Common:TOTAL_DISPENSED')}: {totalAmount}
            </Text>
          </TableCell>
        </TableRow>
      </TableBody>
    </Table>
  )
})

export default DispensedFromSection
